import { SelectOptions } from '@spo/logic';
import { CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select';

const semanticColorVp = (color: string) => {
  return `var(--vp-semantic-color-${color})`;
};

const dropdownIndicatorStyles = {
  color: semanticColorVp('content-neutral'),
  ':hover': {
    color: semanticColorVp('content-quiet'),
  },
  margin: '4px',
  padding: 0,
  cursor: 'pointer',
};

const dropdownIndicator = (provided: CSSObjectWithLabel) =>
  ({
    ...provided,
    ...dropdownIndicatorStyles,
  }) as CSSObjectWithLabel;

const loadingMessage = (provided: CSSObjectWithLabel) =>
  ({
    ...provided,
    textTransform: 'lowercase',
  }) as CSSObjectWithLabel;

const menu = (provided: CSSObjectWithLabel) =>
  ({
    ...provided,
    backgroundColor: semanticColorVp('container-neutral'),
    zIndex: 2,
  }) as CSSObjectWithLabel;

const inputStyles = {
  color: semanticColorVp('content-neutral'),
};

const input = (provided: CSSObjectWithLabel) =>
  ({
    ...provided,
    ...inputStyles,
  }) as CSSObjectWithLabel;

const controlStyles = {
  backgroundColor: semanticColorVp('container-neutral'),
  borderColor: semanticColorVp('border-neutral'),
  padding: '0.5rem 0',
};

const noOptionsMessage = (provided: CSSObjectWithLabel) =>
  ({
    ...provided,
    textTransform: 'lowercase',
  }) as CSSObjectWithLabel;

const valueContainerStyles = {
  gap: '0.25rem',
};

const multiValueStyles: CSSObjectWithLabel = {
  backgroundColor: 'transparent',
  borderColor: semanticColorVp('content-brand'),
  borderWidth: '0.0625rem',
  borderRadius: `${32 / 16}rem`,
  position: 'relative',
  lineHeight: `${32 / 16}rem`,
  margin: '0',
};

const multiValueLabelStyles: CSSObjectWithLabel = {
  color: semanticColorVp('content-brand'),
  padding: '0',
  whiteSpace: 'nowrap',
  paddingLeft: `${12 / 16}rem`,
  fontSize: `${14 / 16}rem`,
};

const multiValueRemoveStyles: CSSObjectWithLabel = {
  whiteSpace: 'normal',
  padding: '0',
  width: `${32 / 16}rem`,
  display: 'flex',
  justifyContent: 'center',
  color: semanticColorVp('content-brand'),
  cursor: 'pointer',
  ':hover': {
    backgroundColor: 'transparent',
    color: semanticColorVp('content-neutral'),
  },
};

export const selectStyles: StylesConfig<
  SelectOptions,
  true,
  GroupBase<SelectOptions>
> = {
  dropdownIndicator,
  indicatorSeparator: () => ({ display: 'none' }),
  clearIndicator: dropdownIndicator,
  loadingMessage,
  menu,
  noOptionsMessage,
  input,
  control: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      ...controlStyles,
    }) as CSSObjectWithLabel,
  valueContainer: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      ...valueContainerStyles,
    }) as CSSObjectWithLabel,
  singleValue: (provided: CSSObjectWithLabel) =>
    ({
      ...provided,
      color: semanticColorVp('content-neutral'),
    }) as CSSObjectWithLabel,
  multiValue: (provided: CSSObjectWithLabel) => ({
    ...provided,
    ...multiValueStyles,
  }),
  multiValueLabel: (provided: CSSObjectWithLabel) => ({
    ...provided,
    ...multiValueLabelStyles,
  }),
  multiValueRemove: (provided: CSSObjectWithLabel) => ({
    ...provided,
    ...multiValueRemoveStyles,
  }),
  option: (
    provided: CSSObjectWithLabel,
    { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean },
  ) =>
    ({
      ...provided,
      backgroundColor:
        (isFocused && semanticColorVp('border-quiet')) ||
        semanticColorVp('container-neutral'),
      color: isSelected
        ? semanticColorVp('content-brand')
        : semanticColorVp('content-neutral'),
    }) as CSSObjectWithLabel,
};

export const selectStylesError: StylesConfig<
  SelectOptions,
  true,
  GroupBase<SelectOptions>
> = {
  ...selectStyles,
  control: (provided) =>
    ({
      ...provided,
      ...controlStyles,
      borderColor: semanticColorVp('status-negative'),
      borderWidth: '0.12rem',
    }) as CSSObjectWithLabel,
};

export const selectFilterStyles: StylesConfig<
  SelectOptions,
  true,
  GroupBase<SelectOptions>
> = {
  ...selectStyles,
  valueContainer: (provided) =>
    ({
      ...provided,
      ...valueContainerStyles,
      textOverflow: 'ellipsis',
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      flexWrap: 'nowrap',
      cursor: 'pointer',
    }) as CSSObjectWithLabel,
};
